import { ReactElement } from 'react'
import {
    ClassName,
    TileClassNameFunc,
} from 'react-calendar/dist/cjs/shared/types'
import { CalendarProps } from './props'
import { StyledCalendar } from './styles'

const sameDay = (d1: Date, d2: Date) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()

const isLastDayOfWeek = (date: Date) => date.getDay() === 0
const isFirstDayOfWeek = (date: Date) => date.getDay() === 1

const addToClass = (className: ClassName, newClass: string) => {
    if (!className || className === null) return newClass
    else if (typeof className === 'string') {
        if (className === '') return newClass
        return `${className} ${newClass}`
    } else {
        className.push(newClass)
        return className
    }
}

const Calendar = ({
    locale = 'da-DK',
    markedDays,
    markedDaysColor,
    markedDaysFromTo,
    minDetail = 'decade',
    showNeighboringMonth = false,
    tileClassName,
    ...props
}: CalendarProps): ReactElement => {
    const tileClassNameUpdated: ClassName | TileClassNameFunc = (rest) => {
        let newClass =
            typeof tileClassName === 'function'
                ? tileClassName(rest)
                : tileClassName ?? ''

        if (markedDays) {
            const dateFinder = Array.isArray(markedDays)
                ? markedDays.find((day) => sameDay(day, rest.date))
                : sameDay(markedDays, rest.date)

            if (dateFinder) newClass = addToClass(newClass, 'markedDays')
        }

        if (markedDaysFromTo) {
            markedDaysFromTo.forEach((day) => {
                const { from, to, className } = day

                if (
                    sameDay(rest.date, from) ||
                    sameDay(rest.date, to) ||
                    (rest.date >= from && rest.date <= to)
                ) {
                    newClass = addToClass(newClass, 'markedDaysFromTo')

                    if (className) newClass = addToClass(newClass, className)

                    if (sameDay(rest.date, from) || isFirstDayOfWeek(rest.date))
                        newClass = addToClass(newClass, 'markedDaysFromToStart')

                    if (sameDay(rest.date, to) || isLastDayOfWeek(rest.date))
                        newClass = addToClass(newClass, 'markedDaysFromToEnd')
                }
            })
        }

        // get next monday
        const nextMonday =
            rest.date.getDate() + ((1 + 7 - rest.date.getDay()) % 7 || 7)

        if (nextMonday <= 8) newClass = addToClass(newClass, 'firstWeek')

        return newClass
    }

    return (
        <StyledCalendar
            {...props}
            locale={locale}
            minDetail={minDetail}
            showNeighboringMonth={showNeighboringMonth}
            tileClassName={tileClassNameUpdated}
            css={{
                ...(markedDays && {
                    '.markedDays:not(:disabled)': {
                        abbr: {
                            bgc: markedDaysColor ?? '$primary300',
                        },
                    },
                }),
                ...(markedDaysFromTo && {
                    '.markedDaysFromTo::after': {
                        content: '""',
                        bgc: markedDaysColor ?? '$primary300',
                        position: 'absolute',
                        h: 'calc(100% - 8px)',
                        w: '100%',
                        zIndex: -1,
                    },

                    '.markedDaysFromToStart::after': {
                        br: '25px 0 0 25px',
                        pr: '50%',
                        right: 0,
                    },

                    '.markedDaysFromToEnd::after': {
                        br: '0 25px 25px 0',
                        left: 0,
                        pl: '50%',
                    },

                    '.markedDaysFromToStart.markedDaysFromToEnd::after': {
                        br: '50%',
                    },
                }),
                ...props.css,
            }}
        />
    )
}

export default Calendar

export { StyledCalendar }
